import { useEffect, useMemo, useState } from "react";
import type {
  AuthMethod,
  CatalogProvider,
  Connection,
  ConnectionScope,
  ManualField,
} from "../types";
import { useConnectFlow } from "./useConnectFlow";

/** The ticked values of a checkbox field, out of the comma-separated string they live in. */
function checkedValues(raw: string | undefined): string[] {
  return (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

/** What a field starts out holding: a checkbox group's defaults, or an empty string. */
function initialValue(field: ManualField): string {
  if (!field.options) return "";
  return field.options
    .filter((option) => option.default)
    .map((option) => option.value)
    .join(",");
}

/** Set when the wizard is repairing an account rather than adding one. */
export interface ReconnectTarget {
  provider: CatalogProvider;
  conn: Connection;
}

interface Props {
  catalog: CatalogProvider[];
  connections: Connection[];
  /** The project a new connection lands in. A reconnect stays where it already is. */
  projectId: string;
  /**
   * The name of the project's team, for the scope choice's label -- or null in a
   * private project, where there is nobody to share with and the choice is not asked.
   */
  teamName: string | null;
  reconnect?: ReconnectTarget | null;
  onClose: () => void;
}

/**
 * The only way an account gets connected: pick a service, pick how to connect it, then
 * authorize or paste the credential.
 *
 * The three questions are asked one at a time, and on purpose. They are not independent:
 * which methods exist depends on the service, and which fields (or which scopes) you are
 * looking at depends on the method. Laid out side by side on a card, the second and third
 * are noise until the first is answered -- so the wizard answers them in the order the
 * user actually has to.
 *
 * Reconnecting is the same walk with the first step already taken: the provider is fixed
 * (a repair cannot change which service the account belongs to) and the method is asked
 * again, because a broken OAuth connection may well be one the user now wants to re-do
 * some other way.
 */
export default function ConnectWizard({
  catalog,
  connections,
  projectId,
  teamName,
  reconnect = null,
  onClose,
}: Props) {
  const [provider, setProvider] = useState<CatalogProvider | null>(
    reconnect?.provider ?? null
  );
  const [method, setMethod] = useState<AuthMethod | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  /**
   * Personal by default: sharing a credential with the whole team is the choice that
   * should have to be made deliberately, not the one you get by not noticing a control.
   */
  const [scope, setScope] = useState<ConnectionScope>("personal");
  /** The success line from the finished attempt. Non-null means we are on the last step. */
  const [done, setDone] = useState<string | null>(null);

  const { busy, error, setError, connect } = useConnectFlow();

  // Connectable first: a service this deployment has no credentials for is worth showing
  // (it says what MochiConnector could do) but never worth putting at the top.
  const providers = useMemo(
    () => [...catalog].sort((a, b) => Number(b.configured) - Number(a.configured)),
    [catalog]
  );

  const methods = provider?.authMethods.filter((m) => m.configured) ?? [];

  const step: "provider" | "method" | "connect" | "done" = done
    ? "done"
    : !provider
      ? "provider"
      : !method
        ? "method"
        : "connect";

  // Escape closes -- but not out from under an OAuth popup the user is still deciding on.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  function pickProvider(next: CatalogProvider) {
    setError(null);
    setProvider(next);
    setMethod(null);
  }

  function pickMethod(next: AuthMethod) {
    setError(null);
    setMethod(next);
    setValues(Object.fromEntries(next.fields.map((f) => [f.key, initialValue(f)])));
  }

  function back() {
    setError(null);
    if (method) {
      setMethod(null);
      return;
    }
    if (provider && !reconnect) setProvider(null);
  }

  /** Back to step one, keeping the wizard open -- "connect another" without a round trip. */
  function restart() {
    setError(null);
    setDone(null);
    setMethod(null);
    setValues({});
    setProvider(null);
  }

  async function submit() {
    if (!provider || !method) return;
    const result = await connect(provider, method, {
      values,
      connectionId: reconnect?.conn.id ?? null,
      // In a private project the scope question was never asked, so never send its
      // answer: everything there is personal by definition.
      ...(reconnect ? {} : { projectId, scope: teamName ? scope : "personal" }),
    });
    if (result.ok) {
      setDone(result.message ?? `${provider.name} connected.`);
    }
  }

  const steps = reconnect
    ? ["How to connect", "Authorize"]
    : ["Choose a service", "How to connect", "Authorize"];
  const stepIndex = reconnect
    ? step === "method"
      ? 0
      : 1
    : step === "provider"
      ? 0
      : step === "method"
        ? 1
        : 2;

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={reconnect ? "Reconnect account" : "Add a provider"}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div>
            <h2>
              {reconnect ? `Reconnect ${reconnect.conn.accountLabel}` : "Add a provider"}
            </h2>
            <p className="modal-sub">
              {step === "done"
                ? "That is the whole setup — your MCP client needs no change."
                : `Step ${stepIndex + 1} of ${steps.length} · ${steps[stepIndex]}`}
            </p>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {step !== "done" && (
          <ol className="steps">
            {steps.map((label, i) => (
              <li
                key={label}
                className={`step ${
                  i < stepIndex ? "step-done" : i === stepIndex ? "step-current" : ""
                }`}
              >
                <span className="step-num">{i < stepIndex ? "✓" : i + 1}</span>
                <span className="step-label">{label}</span>
              </li>
            ))}
          </ol>
        )}

        <div className="modal-body">
          {step === "provider" && (
            <>
              <p className="wizard-note">
                Pick a service. Choosing one you have already connected adds a second,
                independent account rather than replacing the first.
              </p>
              <div className="wizard-list">
                {providers.map((p) => {
                  const count = connections.filter((c) => c.providerId === p.id).length;
                  return (
                    <button
                      key={p.id}
                      className="wizard-option"
                      disabled={!p.configured}
                      onClick={() => pickProvider(p)}
                    >
                      <span className="wizard-icon" aria-hidden="true">
                        {p.icon}
                      </span>
                      <span className="wizard-body">
                        <span className="wizard-title">
                          {p.name}
                          {count > 0 && (
                            <span className="pill pill-xs pill-ok">
                              {count} connected
                            </span>
                          )}
                          {!p.configured && (
                            <span className="pill pill-xs pill-muted">
                              Not configured on this deployment
                            </span>
                          )}
                        </span>
                        <span className="wizard-desc">{p.description}</span>
                      </span>
                      {/* A provider that discovers its tools cannot be counted here: what
                          it exposes is whatever the server the user points it at says. */}
                      <span className="wizard-meta">
                        {p.dynamicTools ? "Its own tools" : `${p.tools.length} tools`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === "method" && provider && (
            <>
              <p className="wizard-note">
                {methods.length === 1
                  ? `${provider.name} connects one way on this deployment.`
                  : `${provider.name} can be connected in more than one way, and the choice decides which tools the account gets.`}
              </p>
              <div className="wizard-list">
                {methods.map((m) => (
                  <button
                    key={m.id}
                    className="wizard-option"
                    onClick={() => pickMethod(m)}
                  >
                    <span className="wizard-body">
                      <span className="wizard-title">{m.label}</span>
                      <span className="wizard-desc">{m.description}</span>
                    </span>
                    {m.toolNames.length > 0 && (
                      <span className="wizard-meta">
                        {m.toolNames.length} tool{m.toolNames.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "connect" && provider && method && method.kind === "oauth2" && (
            <div className="wizard-connect">
              <p className="wizard-note">
                A {provider.name} window opens so you can authorize MochiConnector.
                Nothing is stored until you approve it.
              </p>

              {!reconnect && teamName && (
                <ScopePicker scope={scope} teamName={teamName} onChange={setScope} />
              )}

              {method.scopes.length > 0 && (
                <div className="wizard-scopes">
                  <span className="wizard-scopes-label">It will ask for</span>
                  {method.scopes.map((scope) => (
                    <code key={scope}>{scope}</code>
                  ))}
                </div>
              )}

              {error && <p className="manual-error">{error}</p>}

              <button className="btn" onClick={submit} disabled={busy}>
                {busy
                  ? `Waiting for ${provider.name}…`
                  : `Authorize with ${provider.name}`}
              </button>
            </div>
          )}

          {step === "connect" && provider && method && method.kind === "manual" && (
            <form
              className="wizard-connect"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <p className="wizard-note">{method.description}</p>

              {!reconnect && teamName && (
                <ScopePicker scope={scope} teamName={teamName} onChange={setScope} />
              )}

              {method.fields.map((field) =>
                field.options ? (
                  <CheckboxField
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    disabled={busy}
                    onChange={(next) =>
                      setValues((v) => ({ ...v, [field.key]: next }))
                    }
                  />
                ) : (
                  <label key={field.key} className="manual-field">
                    <span className="manual-label">{field.label}</span>
                    {field.multiline ? (
                      <textarea
                        value={values[field.key] ?? ""}
                        placeholder={field.placeholder}
                        rows={3}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={busy}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, [field.key]: e.target.value }))
                        }
                      />
                    ) : (
                      <input
                        type={field.secret ? "password" : "text"}
                        value={values[field.key] ?? ""}
                        placeholder={field.placeholder}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={busy}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, [field.key]: e.target.value }))
                        }
                      />
                    )}
                    {field.help && <span className="manual-help">{field.help}</span>}
                  </label>
                )
              )}

              {error && <p className="manual-error">{error}</p>}

              <button
                className="btn"
                type="submit"
                disabled={busy || !values[method.fields[0].key]?.trim()}
              >
                {busy ? "Connecting…" : "Connect"}
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="wizard-done">
              <span className="wizard-done-mark" aria-hidden="true">
                ✓
              </span>
              <p className="wizard-done-line">{done}</p>
              <p className="wizard-note">
                {reconnect
                  ? "Its tools keep the names they had, so nothing on the client side changes."
                  : "Switch it on to expose its tools. Your MCP URL and token stay as they are."}
              </p>
            </div>
          )}
        </div>

        <footer className="modal-foot">
          {step === "done" ? (
            <>
              {!reconnect && (
                <button className="btn btn-ghost" onClick={restart}>
                  Add another
                </button>
              )}
              <button className="btn" onClick={onClose}>
                Done
              </button>
            </>
          ) : (
            <>
              {stepIndex > 0 && (
                <button className="btn btn-ghost" onClick={back} disabled={busy}>
                  Back
                </button>
              )}
              <button className="btn btn-subtle" onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

/**
 * A field that is a set rather than a string: which of a fixed list the account may use.
 *
 * The same rows as the tool dialog, deliberately -- ticking a box to decide what an
 * account exposes is one gesture in this product, and it should not look like two. The
 * difference is when it is asked: a tool can be toggled any time because the answer lives
 * on a document we own, while this is part of the credential and so is settled here, with
 * the key. Changing it later means reconnecting.
 *
 * The order written back is the field's declaration order, never the click order. A
 * provider is entitled to read meaning into the first ticked value -- Gemini's default
 * model is exactly that -- and "whichever you clicked first" would make that arbitrary.
 */
function CheckboxField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: ManualField;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const options = field.options ?? [];
  const checked = new Set(checkedValues(value));

  function toggle(optionValue: string, on: boolean) {
    const next = new Set(checked);
    if (on) next.add(optionValue);
    else next.delete(optionValue);
    onChange(
      options
        .filter((option) => next.has(option.value))
        .map((option) => option.value)
        .join(",")
    );
  }

  return (
    <fieldset className="manual-field manual-checks">
      <legend className="manual-label">{field.label}</legend>
      {field.help && <span className="manual-help manual-checks-help">{field.help}</span>}
      <div className="tools">
        {options.map((option) => (
          <label key={option.value} className="tool">
            <input
              type="checkbox"
              checked={checked.has(option.value)}
              disabled={disabled}
              onChange={(e) => toggle(option.value, e.target.checked)}
            />
            <span className="tool-body">
              <span className="tool-name">
                <code>{option.label}</code>
              </span>
              {option.help && <span className="tool-desc">{option.help}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Team-level or personal. Asked on the authorize step -- it is part of the same
 * decision as the credential itself, and cannot be changed afterwards: moving an
 * already-shared credential back to personal (or quietly promoting a personal one)
 * is exactly the surprise this control exists to prevent.
 */
function ScopePicker({
  scope,
  teamName,
  onChange,
}: {
  scope: ConnectionScope;
  teamName: string;
  onChange: (scope: ConnectionScope) => void;
}) {
  return (
    <fieldset className="scope-picker">
      <legend className="manual-label">Who can use it</legend>
      <label className="scope-option">
        <input
          type="radio"
          name="connection-scope"
          checked={scope === "personal"}
          onChange={() => onChange("personal")}
        />
        <span>
          <strong>Just me</strong> — only your MCP server for this project sees its
          tools.
        </span>
      </label>
      <label className="scope-option">
        <input
          type="radio"
          name="connection-scope"
          checked={scope === "team"}
          onChange={() => onChange("team")}
        />
        <span>
          <strong>Everyone in {teamName}</strong> — every member's server for this
          project gets its tools, through the account you authorize here.
        </span>
      </label>
    </fieldset>
  );
}
